import type { InspectionReport } from '@devrobot/shared';
import { CircleCheck, Download, OctagonAlert, TriangleAlert } from 'lucide-react';
import { formatBytes } from '../lib/format';
import { KeyValue } from './KeyValue';

const yesNo = (v: boolean) => (v ? 'yes' : 'no');

/** Host inspection gathered before enrollment installs anything (FR-VPS-01). */
export function InspectionReportView({ report }: { report: InspectionReport }) {
  // Problems with a remedy are fixed by enrollment itself after approval (e.g. Docker is installed).
  const blockers = report.problems.filter((p) => p.severity === 'blocker' && !p.remedy);
  const fixes = report.problems.filter((p) => p.remedy);
  const warnings = report.problems.filter((p) => p.severity === 'warning' && !p.remedy);
  const installsDocker = fixes.some((p) => p.remedy === 'install_docker');
  return (
    <div className="space-y-5">
      {report.problems.length > 0 ? (
        <div className="space-y-2">
          <h4 className="font-semibold">Problems found</h4>
          <ul className="divide-y divide-hairline rounded-field border border-hairline">
            {[...blockers, ...fixes, ...warnings].map((p, i) => {
              const Icon = p.remedy ? Download : p.severity === 'blocker' ? OctagonAlert : TriangleAlert;
              return (
                <li key={i} className="flex items-start gap-2.5 px-3 py-2.5 text-sm max-sm:flex-col max-sm:gap-1.5">
                  <span className={`badge badge-soft badge-sm shrink-0 gap-1 ${p.remedy ? 'badge-info' : p.severity === 'blocker' ? 'badge-error' : 'badge-warning'}`}>
                    <Icon className="size-3" aria-hidden="true" />
                    {p.remedy ? 'will be installed' : p.severity}
                  </span>
                  <span className="break-anywhere">
                    {p.message} <span className="font-mono text-xs text-base-content/50">({p.code})</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div role="status" className="alert alert-success alert-soft text-sm">
          <CircleCheck className="size-4 shrink-0" aria-hidden="true" />
          No blocking problems were found on this host.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-2 rounded-field bg-base-200/60 p-3">
          <h4 className="text-xs tracking-wide text-base-content/55 uppercase">System</h4>
          <KeyValue
            items={[
              { label: 'Hostname', value: <span className="font-mono">{report.hostname}</span> },
              { label: 'Operating system', value: `${report.os.prettyName} (${report.os.id} ${report.os.versionId})` },
              { label: 'Kernel', value: report.os.kernel },
              { label: 'Architecture', value: report.arch },
              { label: 'Running as root', value: yesNo(report.isRoot) },
              { label: 'systemd', value: yesNo(report.systemd) },
              { label: 'Inspector version', value: report.agentVersion },
            ]}
          />
        </div>
        <div className="space-y-2 rounded-field bg-base-200/60 p-3">
          <h4 className="text-xs tracking-wide text-base-content/55 uppercase">Runtime and resources</h4>
          <KeyValue
            items={[
              {
                label: 'Docker',
                value: report.docker.installed
                  ? `${report.docker.version ?? 'installed'}${report.docker.serverReachable ? '' : ' (daemon not reachable)'}`
                  : installsDocker
                    ? 'not installed (enrollment installs it)'
                    : 'not installed',
              },
              { label: 'Docker error', value: report.docker.error, hidden: !report.docker.error },
              {
                label: 'Compose',
                value: report.compose.installed
                  ? `${report.compose.version ?? 'installed'}${report.compose.supportsOverrideTags ? '' : ' (no override tag support)'}`
                  : installsDocker
                    ? 'not installed (enrollment installs it)'
                    : 'not installed',
              },
              { label: 'CPUs', value: report.resources.cpus },
              { label: 'Memory', value: `${formatBytes(report.resources.memoryAvailableBytes)} free of ${formatBytes(report.resources.memoryBytes)}` },
              { label: 'Disk', value: `${formatBytes(report.resources.diskFreeBytes)} free of ${formatBytes(report.resources.diskTotalBytes)}` },
              { label: 'Data directory', value: <span className="font-mono">{report.resources.dataDir}</span> },
            ]}
          />
        </div>
      </div>

      <div className="space-y-2 rounded-field bg-base-200/60 p-3">
        <h4 className="text-xs tracking-wide text-base-content/55 uppercase">Public HTTP/HTTPS ports</h4>
        <KeyValue
          items={[
            { label: `Port ${report.edge.httpPort}`, value: report.edge.httpOwner ? `in use by ${report.edge.httpOwner}` : 'free' },
            { label: `Port ${report.edge.httpsPort}`, value: report.edge.httpsOwner ? `in use by ${report.edge.httpsOwner}` : 'free' },
            {
              label: 'Existing DevRobot agent',
              value: report.existingAgent.installed ? `installed${report.existingAgent.version ? ` ${report.existingAgent.version}` : ''}${report.existingAgent.serverUrl ? ` (server ${report.existingAgent.serverUrl})` : ''}` : 'none',
            },
          ]}
        />
      </div>

      <div className="space-y-2">
        <h4 className="text-xs tracking-wide text-base-content/55 uppercase">Existing containers ({report.containers.length})</h4>
        {report.containers.length === 0 ? (
          <p className="text-sm text-base-content/60">No containers are running on this host.</p>
        ) : (
          <div className="overflow-x-auto rounded-field border border-hairline">
            <table className="table table-xs">
              <thead>
                <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                  <th>Name</th>
                  <th>Image</th>
                  <th>State</th>
                  <th className="max-md:hidden">Ports</th>
                  <th className="max-lg:hidden">Compose project</th>
                  <th>DevRobot stack</th>
                </tr>
              </thead>
              <tbody>
                {report.containers.map((c) => (
                  <tr key={c.id} className="hover:bg-base-200/50">
                    <td className="font-mono">{c.name}</td>
                    <td className="font-mono break-anywhere text-base-content/70">{c.image}</td>
                    <td>{c.state}</td>
                    <td className="font-mono break-anywhere max-md:hidden">{c.ports || '—'}</td>
                    <td className="max-lg:hidden">{c.composeProject ?? '—'}</td>
                    <td>{c.devrobotStack ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-base-content/60">DevRobot manages only the stacks it creates; existing containers are left untouched.</p>
      </div>
    </div>
  );
}
